import { useCallback, useEffect, useMemo, useState } from "react";
import { EMAILS_DEFAULT_DAYS, type SentEmail } from "@site-alerter/shared";
import { getEmails } from "../api.js";
import { absoluteTime, relativeTime } from "../time.js";

interface Batch {
  key: string;
  sentAt: string;
  subject: string;
  trigger: string;
  siteId: string;
  bodySnippet: string;
  recipients: string[];
  messageIds: string[];
}

/**
 * One worker run sends one message per recipient, so the same alert arrives as
 * several SENT rows. Collapse them back into the single event they describe.
 *
 * Rows written before `batchId` existed fall back to a subject-and-minute key,
 * which groups them correctly in every case except two identical alerts inside
 * the same minute — impossible at a one-minute schedule.
 */
function groupIntoBatches(items: SentEmail[]): Batch[] {
  const order: string[] = [];
  const byKey = new Map<string, Batch>();

  for (const item of items) {
    const key = item.batchId ?? `${item.siteId}|${item.subject}|${item.sentAt.slice(0, 16)}`;
    const existing = byKey.get(key);
    if (existing == null) {
      order.push(key);
      byKey.set(key, {
        key,
        sentAt: item.sentAt,
        subject: item.subject,
        trigger: item.trigger,
        siteId: item.siteId,
        bodySnippet: item.bodySnippet,
        recipients: [...item.recipients],
        messageIds: [item.sesMessageId],
      });
      continue;
    }
    existing.recipients.push(...item.recipients);
    existing.messageIds.push(item.sesMessageId);
    // Rows arrive newest-first; the batch is stamped with when it started.
    if (item.sentAt < existing.sentAt) existing.sentAt = item.sentAt;
  }

  return order.map((key) => byKey.get(key)!);
}

export function SentHistory({ idToken }: { idToken: string }): JSX.Element {
  const [items, setItems] = useState<SentEmail[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (from: string | null, append: boolean) => {
      setBusy(true);
      setError(null);
      try {
        const page = await getEmails(idToken, EMAILS_DEFAULT_DAYS, from);
        setItems((previous) => (append ? [...previous, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setLoaded(true);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setBusy(false);
      }
    },
    [idToken],
  );

  useEffect(() => {
    void load(null, false);
  }, [load]);

  const batches = useMemo(() => groupIntoBatches(items), [items]);

  return (
    <section className="panel panel-wide">
      <h2>Sent in the last {EMAILS_DEFAULT_DAYS} days</h2>

      {error != null && <p className="error">{error}</p>}

      {items.length === 0 && loaded && !busy ? (
        <p className="muted">No emails sent in this window.</p>
      ) : (
        <ol className="sent">
          {batches.map((batch) => (
            <li key={batch.key}>
              <div className="sent-head">
                <span className="subject">{batch.subject}</span>
                <time dateTime={batch.sentAt} title={absoluteTime(batch.sentAt)}>
                  {relativeTime(batch.sentAt)}
                </time>
              </div>
              <div className="muted small">
                {batch.trigger} · sent to {batch.recipients.length}{" "}
                {batch.recipients.length === 1 ? "recipient" : "recipients"}
              </div>
              <p className="snippet">{batch.bodySnippet}</p>
              <details className="sent-detail">
                <summary className="muted small">Delivery detail</summary>
                <ul className="muted small delivery">
                  {batch.recipients.map((recipient, index) => (
                    <li key={`${batch.key}-${recipient}`}>
                      <span>{recipient}</span>
                      <code>{batch.messageIds[index]}</code>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ol>
      )}

      {cursor != null && (
        <button type="button" disabled={busy} onClick={() => void load(cursor, true)}>
          {busy ? "Loading…" : "Load more"}
        </button>
      )}
    </section>
  );
}
